import React, { useState, useEffect } from 'react';
import { 
  Pill, 
  Calendar, 
  Activity, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Plus,
  TrendingUp,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalMedications: 5,
    activeReminders: 3,
    upcomingAppointments: 2,
    healthScore: 85
  });

  const [recentMedications, setRecentMedications] = useState([
    {
      id: 1,
      name: 'Aspirin',
      dosage: '100mg',
      time: '09:00',
      taken: true,
      type: 'daily'
    },
    {
      id: 2,
      name: 'Vitamin D',
      dosage: '1000IU',
      time: '12:00',
      taken: false,
      type: 'daily'
    },
    {
      id: 3,
      name: 'Metformin',
      dosage: '500mg',
      time: '18:00',
      taken: false,
      type: 'daily'
    }
  ]);

  const [upcomingAppointments, setUpcomingAppointments] = useState([
    {
      id: 1,
      doctor: 'Dr. Sarah Johnson',
      specialty: 'Cardiology',
      date: '2024-01-15',
      time: '14:30',
      location: 'Medical Center'
    },
    {
      id: 2,
      doctor: 'Dr. Michael Chen',
      specialty: 'Dermatology',
      date: '2024-01-18',
      time: '10:00',
      location: 'Skin Clinic'
    }
  ]);

  // Add Medicine Modal State
  const [showAddMedicineModal, setShowAddMedicineModal] = useState(false);
  const [newMedicine, setNewMedicine] = useState({
    name: '',
    dosage: '',
    time: '',
    frequency: 'daily',
    instructions: ''
  });

  const markMedicationTaken = (id) => {
    setRecentMedications(prev => 
      prev.map(med => 
        med.id === id ? { ...med, taken: !med.taken } : med
      )
    );
    toast.success('Medication status updated!');
  };

  const handleAddMedicine = () => {
    if (!newMedicine.name || !newMedicine.dosage || !newMedicine.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    const medicine = {
      id: Date.now(),
      ...newMedicine,
      taken: false,
      type: newMedicine.frequency
    };

    setRecentMedications([...recentMedications, medicine]);
    setStats(prev => ({
      ...prev,
      totalMedications: prev.totalMedications + 1,
      activeReminders: prev.activeReminders + 1
    }));

    // Reset form
    setNewMedicine({
      name: '',
      dosage: '',
      time: '',
      frequency: 'daily',
      instructions: ''
    });
    setShowAddMedicineModal(false);
    toast.success('Medicine added successfully!');
  };

  const handleInputChange = (field, value) => {
    setNewMedicine(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getTimeStatus = (time) => {
    const now = new Date();
    const [hours, minutes] = time.split(':');
    const medTime = new Date();
    medTime.setHours(parseInt(hours), parseInt(minutes), 0);
    
    const diff = now - medTime;
    const diffMinutes = Math.floor(diff / (1000 * 60));
    
    if (diffMinutes < -30) return 'upcoming';
    if (diffMinutes > 30) return 'overdue';
    return 'due';
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Welcome back!</h1>
        <p className="page-subtitle">Here's your health overview for today</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Pill size={32} color="#667eea" />
          </div>
          <div className="stat-value">{stats.totalMedications}</div>
          <div className="stat-label">Active Medications</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Clock size={32} color="#ff6b6b" />
          </div>
          <div className="stat-value">{stats.activeReminders}</div>
          <div className="stat-label">Today's Reminders</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={32} color="#4ecdc4" />
          </div>
          <div className="stat-value">{stats.upcomingAppointments}</div>
          <div className="stat-label">Upcoming Appointments</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={32} color="#45b7d1" />
          </div>
          <div className="stat-value">{stats.healthScore}%</div>
          <div className="stat-label">Health Score</div>
        </div>
      </div>

      <div className="content-grid">
        <div className="main-content-section">
          <div className="section-header">
            <h2 className="section-title">Today's Medications</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddMedicineModal(true)}
            >
              <Plus size={16} />
              Add Medication
            </button>
          </div>

          <div className="medications-list">
            {recentMedications.map((medication) => {
              const timeStatus = getTimeStatus(medication.time);
              return (
                <div key={medication.id} className="medication-item">
                  <div className="medication-info">
                    <div className="medication-name">{medication.name}</div>
                    <div className="medication-details">
                      {medication.dosage} • {medication.time}
                    </div>
                    <div className={`time-status ${timeStatus}`}>
                      {timeStatus === 'overdue' && <AlertCircle size={16} />}
                      {timeStatus === 'due' && <Clock size={16} />}
                      {timeStatus === 'upcoming' && <Clock size={16} />}
                      {timeStatus === 'overdue' ? 'Overdue' : 
                       timeStatus === 'due' ? 'Due now' : 'Upcoming'}
                    </div>
                  </div>
                  
                  <button
                    className={`medication-action ${medication.taken ? 'taken' : ''}`}
                    onClick={() => markMedicationTaken(medication.id)}
                  >
                    {medication.taken ? (
                      <CheckCircle size={20} color="#2ecc71" />
                    ) : (
                      <Plus size={20} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="sidebar-content">
          <div className="section-header">
            <h2 className="section-title">Upcoming Appointments</h2>
          </div>

          <div className="appointments-list">
            {upcomingAppointments.map((appointment) => (
              <div key={appointment.id} className="appointment-item">
                <div className="appointment-date">
                  <div className="date-day">
                    {format(new Date(appointment.date), 'dd')}
                  </div>
                  <div className="date-month">
                    {format(new Date(appointment.date), 'MMM')}
                  </div>
                </div>
                
                <div className="appointment-details">
                  <div className="appointment-doctor">{appointment.doctor}</div>
                  <div className="appointment-specialty">{appointment.specialty}</div>
                  <div className="appointment-time">
                    {appointment.time} • {appointment.location}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="quick-actions">
            <h3 className="section-title">Quick Actions</h3>
            <div className="action-buttons">
              <button className="btn btn-secondary">
                <Activity size={16} />
                Log Health Data
              </button>
              <button className="btn btn-secondary">
                <Calendar size={16} />
                Schedule Appointment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Medicine Modal */}
      {showAddMedicineModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Medication</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddMedicineModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Medication Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newMedicine.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Aspirin"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dosage *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newMedicine.dosage}
                  onChange={(e) => handleInputChange('dosage', e.target.value)}
                  placeholder="e.g., 100mg"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Time *</label>
                <input
                  type="time"
                  className="form-input"
                  value={newMedicine.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Frequency</label>
                <select
                  className="form-input"
                  value={newMedicine.frequency}
                  onChange={(e) => handleInputChange('frequency', e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="twice daily">Twice Daily</option>
                  <option value="three times daily">Three Times Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="as needed">As Needed</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Instructions</label>
                <textarea
                  className="form-input"
                  value={newMedicine.instructions}
                  onChange={(e) => handleInputChange('instructions', e.target.value)}
                  placeholder="e.g., Take with food"
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddMedicineModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddMedicine}
              >
                Add Medication
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 